import noop from 'lodash/noop';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { uniqueId } from '../../../../lib/uniqueId';

interface ReserveSpaceForPanelsCtxProps {
  currentLeftOpenPanelId: React.RefObject<string | null>;
  leftReservedSpace: number;
  setLeftReservedSpace: React.Dispatch<React.SetStateAction<number>>;
  isLocalExpandedMode: boolean;
  setIsLocalExpandedMode: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ReserveSpaceForPanelsCtx = createContext<ReserveSpaceForPanelsCtxProps>({
  leftReservedSpace: 0,
  setLeftReservedSpace: noop,
  currentLeftOpenPanelId: { current: null },
  isLocalExpandedMode: false,
  setIsLocalExpandedMode: noop,
});

export const ReserveSpaceForPanelsProvider = ({ children }: { children: React.ReactNode }) => {
  const [leftReservedSpace, setLeftReservedSpace] = useState<number>(0);
  const [isLocalExpandedMode, setIsLocalExpandedMode] = useState(false);

  const currentLeftOpenPanelId = useRef<string>(null);

  return (
    <ReserveSpaceForPanelsCtx.Provider
      value={{
        leftReservedSpace,
        setLeftReservedSpace,
        currentLeftOpenPanelId,
        isLocalExpandedMode,
        setIsLocalExpandedMode,
      }}
    >
      {children}
    </ReserveSpaceForPanelsCtx.Provider>
  );
};

export const useReserveSpaceForLeftPanel = (size: number) => {
  const { setLeftReservedSpace, currentLeftOpenPanelId } = useContext(ReserveSpaceForPanelsCtx);
  const ref = useRef<string>(null);

  useEffect(() => {
    ref.current = uniqueId();
    currentLeftOpenPanelId.current = ref.current;
    setLeftReservedSpace(size);

    return () => {
      if (ref.current === currentLeftOpenPanelId?.current) {
        setLeftReservedSpace(0);
      }
    };
  }, [currentLeftOpenPanelId, setLeftReservedSpace, size]);
};

export const useGetReservedSpaceForPanels = () => {
  const { leftReservedSpace } = useContext(ReserveSpaceForPanelsCtx);
  return { leftReservedSpace };
};
